import type { MonetizationTopsData, TopMonetizationItem } from '@/mocks/monetizationAnalytics';

interface MonetizationTopsSectionProps {
  data: MonetizationTopsData;
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const tabConfig: { key: string; label: string; icon: string }[] = [
  { key: 'campanas', label: 'Campañas', icon: 'ri-megaphone-line' },
  { key: 'patrocinadores', label: 'Patrocinadores', icon: 'ri-shake-hands-line' },
  { key: 'marketplace', label: 'Marketplace', icon: 'ri-store-2-line' },
];

const trendIcons: Record<string, string> = {
  up: 'ri-arrow-up-line',
  down: 'ri-arrow-down-line',
  flat: 'ri-subtract-line',
};

const trendColors: Record<string, string> = {
  up: 'text-emerald-500 bg-emerald-50',
  down: 'text-red-500 bg-red-50',
  flat: 'text-foreground-400 bg-foreground-100',
};

const rankMedals: Record<number, string> = {
  1: 'bg-amber-400 text-white',
  2: 'bg-slate-300 text-white',
  3: 'bg-amber-600 text-white',
};

function TopRow({ item, maxValue }: { item: TopMonetizationItem; maxValue: number }) {
  const barWidth = maxValue > 0 ? (item.value / maxValue) * 100 : 0;

  return (
    <div className="group">
      <div className="flex items-center gap-3 mb-1.5">
        <div
          className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0 ${
            rankMedals[item.rank] || 'bg-foreground-100 text-foreground-500'
          }`}
        >
          {item.rank}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-foreground-800 text-xs font-medium truncate">
              {item.name}
            </span>
            <span
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold shrink-0 ${trendColors[item.trend]}`}
            >
              <i className={`${trendIcons[item.trend]} text-[8px]`}></i>
            </span>
          </div>
          {item.subtitle && (
            <p className="text-foreground-400 text-[10px] mt-0.5 truncate">{item.subtitle}</p>
          )}
        </div>

        {item.extraLabel && item.extraValue && (
          <span className="text-foreground-400 text-[10px] shrink-0 text-right min-w-[70px]">
            {item.extraLabel}: <strong className="text-foreground-700">{item.extraValue}</strong>
          </span>
        )}

        <span className="text-foreground-950 text-xs font-bold shrink-0 text-right min-w-[70px]">
          ${item.value.toLocaleString()}
        </span>
      </div>

      <div className="w-full h-1.5 rounded-full bg-background-100 overflow-hidden ml-[52px]">
        <div
          className="h-full rounded-full bg-indigo-500 transition-all duration-700 ease-out"
          style={{ width: `${barWidth}%` }}
        ></div>
      </div>
    </div>
  );
}

export default function MonetizationTopsSection({ data, activeTab, onTabChange }: MonetizationTopsSectionProps) {
  const currentList: TopMonetizationItem[] = (data as Record<string, TopMonetizationItem[]>)[activeTab] || [];
  const maxValue = currentList.length > 0 ? Math.max(...currentList.map((i) => i.value)) : 1;

  return (
    <div className="bg-background-50 rounded-xl border border-background-200 p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
            <i className="ri-trophy-line text-amber-500 text-sm"></i>
          </div>
          <div>
            <h3 className="text-foreground-950 font-bold text-sm">Ranking de Monetización</h3>
            <p className="text-foreground-400 text-[10px]">Top performers por categoría de ingreso</p>
          </div>
        </div>

        <div className="flex items-center bg-background-100 rounded-full p-1">
          {tabConfig.map((tab) => (
            <button
              key={tab.key}
              onClick={() => onTabChange(tab.key)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-background-50 text-foreground-950 shadow-sm'
                  : 'text-foreground-400 hover:text-foreground-600'
              }`}
            >
              <i className={`${tab.icon} text-[10px]`}></i>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {currentList.length > 0 ? (
          currentList.map((item) => (
            <TopRow key={`${activeTab}-${item.rank}`} item={item} maxValue={maxValue} />
          ))
        ) : (
          <div className="text-center py-10">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-foreground-100 flex items-center justify-center">
              <i className="ri-information-line text-foreground-400 text-lg"></i>
            </div>
            <p className="text-foreground-400 text-xs">Sin datos disponibles para esta categoría</p>
          </div>
        )}
      </div>
    </div>
  );
}